import { createFileRoute, Link } from '@tanstack/react-router'
import { Shield } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrentUser } from '@/hooks/use-current-user'

export const Route = createFileRoute('/_authed/profile/')({
  component: ProfileIndex,
})

const roleBadgeVariant: Record<string, 'default' | 'secondary' | 'destructive'> = {
  admin: 'default',
  user: 'secondary',
  read_only: 'secondary',
}

function ProfileIndex() {
  const { data: user, isLoading, error } = useCurrentUser()

  return (
    <div className="p-8">
      {/* Slice 1B-1: "Profile" is titled by the ONE shared header. */}
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Your account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-64" />
              <Skeleton className="h-5 w-32" />
            </>
          )}
          {error && (
            <p className="text-sm text-destructive">
              Failed to load profile: {error.message}
            </p>
          )}
          {user && (
            <>
              <Row label="Name" value={user.full_name ?? '—'} />
              <Row label="Email" value={user.email} />
              <div className="flex items-center gap-4">
                <span className="w-20 text-sm font-medium text-muted-foreground">Role</span>
                <Badge variant={roleBadgeVariant[user.role] ?? 'secondary'}>{user.role}</Badge>
              </div>
              <Row label="Active" value={user.is_active ? 'Yes' : 'No'} />
            </>
          )}
          <div className="pt-2 border-t">
            <Button variant="outline" size="sm" asChild>
              <Link to="/profile/security">
                <Shield className="mr-2 h-4 w-4" />
                Change password
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-20 text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  )
}
